import { useState } from 'react'
import { usePricebookEntryBatches, useRevertPricebookEntryBatch } from '@/hooks/use-api'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useToast } from '@/hooks/use-toast'
import { Undo2, Clock } from 'lucide-react'
import { toDisplayDate } from '@/lib/utils'
import type { PricebookEntryBatch } from '@/types'
import { explainError } from '@/lib/explain-error'

interface PricebookChangeHistoryPanelProps {
  pricebookId: string
  disabled?: boolean // disabled while edit mode has unsaved changes (can't revert mid-edit)
}

export function PricebookChangeHistoryPanel({
  pricebookId,
  disabled,
}: PricebookChangeHistoryPanelProps) {
  const { toast } = useToast()
  const { data: batches = [], isLoading } = usePricebookEntryBatches(pricebookId)
  const revertMutation = useRevertPricebookEntryBatch()

  const [pendingBatch, setPendingBatch] = useState<PricebookEntryBatch | null>(null)
  const [conflictIds, setConflictIds] = useState<string[] | null>(null)

  const doRevert = async (batch: PricebookEntryBatch, force = false) => {
    try {
      const res = await revertMutation.mutateAsync({ pricebookId, batchId: batch.id, force })
      toast({
        title: 'Batch reverted',
        description: `Reversed ${res.change_count} change${res.change_count === 1 ? '' : 's'}.`,
      })
      setPendingBatch(null)
      setConflictIds(null)
    } catch (err) {
      const axErr = err as {
        response?: {
          status?: number
          data?: { error?: string; conflicting_ids?: string[]; message?: string }
        }
      }
      if (axErr?.response?.status === 409 && axErr.response.data?.conflicting_ids) {
        // Surface the conflict to the user; they choose to force or cancel.
        setConflictIds(axErr.response.data.conflicting_ids)
        return
      }
      toast({
        title: 'Failed to revert',
        description: axErr?.response?.data?.error || explainError(err, 'Unknown error'),
        variant: 'destructive',
      })
      setPendingBatch(null)
      setConflictIds(null)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Clock className="h-4 w-4" aria-hidden="true" />
          Recent changes
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <p className="text-sm text-muted-foreground p-4">Loading history…</p>
        ) : batches.length === 0 ? (
          <p className="text-sm text-muted-foreground p-4">No saved changes yet.</p>
        ) : (
          <ul className="divide-y">
            {batches.map((b) => {
              const isRevert = !!b.revert_batch_id
              const isReverted = !!b.reverted_at
              return (
                <li key={b.id} className="flex items-center justify-between gap-3 p-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">
                        {b.change_count} change{b.change_count === 1 ? '' : 's'}
                      </span>
                      {isRevert && (
                        <Badge variant="outline" className="text-[10px]">
                          Revert
                        </Badge>
                      )}
                      {isReverted && (
                        <Badge variant="secondary" className="text-[10px]">
                          Reverted
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                      {b.applied_by_name || 'Unknown'} ·{' '}
                      {(toDisplayDate(b.applied_at) ?? new Date(b.applied_at)).toLocaleString()}
                    </p>
                  </div>
                  {!isReverted && !isRevert && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPendingBatch(b)}
                      disabled={disabled || revertMutation.isPending}
                      aria-label={`Revert batch from ${(toDisplayDate(b.applied_at) ?? new Date(b.applied_at)).toLocaleString()}`}
                      title={
                        disabled
                          ? 'Save or discard your current changes before reverting a past batch.'
                          : undefined
                      }
                    >
                      <Undo2 className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
                      Revert
                    </Button>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </CardContent>

      {/* Standard revert confirmation */}
      <AlertDialog
        open={!!pendingBatch && !conflictIds}
        onOpenChange={(open) => {
          if (!open) setPendingBatch(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revert this batch?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingBatch && (
                <>
                  This will reverse {pendingBatch.change_count} change
                  {pendingBatch.change_count === 1 ? '' : 's'} applied by{' '}
                  {pendingBatch.applied_by_name || 'an unknown user'} on{' '}
                  {(
                    toDisplayDate(pendingBatch.applied_at) ?? new Date(pendingBatch.applied_at)
                  ).toLocaleString()}
                  . A new batch entry will record the revert itself, and the original batch will be
                  marked as reverted.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => pendingBatch && doRevert(pendingBatch, false)}>
              Revert
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Conflict-force confirmation */}
      <AlertDialog
        open={!!conflictIds}
        onOpenChange={(open) => {
          if (!open) {
            setConflictIds(null)
            setPendingBatch(null)
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Overwrite newer changes?</AlertDialogTitle>
            <AlertDialogDescription>
              {conflictIds && conflictIds.length > 0 && (
                <>
                  {conflictIds.length} entry{conflictIds.length === 1 ? '' : 'ies'} have been
                  modified since this batch was applied. Reverting now will discard those newer
                  changes:
                  <span className="block mt-2 text-xs font-mono bg-muted p-2 rounded max-h-32 overflow-y-auto">
                    {conflictIds.join(', ')}
                  </span>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => pendingBatch && doRevert(pendingBatch, true)}
            >
              Revert anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}
